import { useEffect, useState, useMemo, useRef } from "react";
import { api, num } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "./ui/dialog";
import { Search, ArrowRight, Lock } from "lucide-react";

/**
 * Global search across ALL entries in the current workspace (respects live/demo mode via api interceptor).
 * Matches counterparty name, note, or amount substring.
 * Click a result → navigates to /entries with that account selected (via URL hash).
 */
export default function GlobalSearch({ open, onClose }) {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setQ("");
    setLoading(true);
    Promise.all([
      api.get("/entries", { params: { status: "all" } }),
      api.get("/accounts"),
    ])
      .then(([e, a]) => {
        setEntries(e.data.entries || []);
        setAccounts(a.data.accounts || []);
      })
      .finally(() => setLoading(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const nameById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a.name])),
    [accounts]
  );

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return entries
      .filter((e) => {
        const fn = (nameById[e.from_account_id] || "").toLowerCase();
        const tn = (nameById[e.to_account_id] || "").toLowerCase();
        const note = (e.note || "").toLowerCase();
        const amt = String(Math.abs(e.amount));
        return (
          fn.includes(query) ||
          tn.includes(query) ||
          note.includes(query) ||
          amt.includes(query)
        );
      })
      .slice(0, 100);
  }, [q, entries, nameById]);

  const goto = (accountId) => {
    // Emit a custom event that Entries page listens for and set localStorage
    localStorage.setItem("entries-goto-account", accountId);
    onClose();
    navigate("/entries");
    // Force page-level reload if already on entries
    setTimeout(() => window.dispatchEvent(new Event("entries-goto")), 50);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="glass-strong rounded-3xl max-w-2xl p-0 overflow-hidden"
        data-testid="global-search-dialog"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-200/60">
          <Search size={18} className="text-stone-400" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search across all entries by name, note, or amount…"
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="global-search-input"
          />
          <span className="text-xs text-stone-400">Esc to close</span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <div className="text-center py-10 text-stone-400 text-sm">Loading…</div>}
          {!loading && q && results.length === 0 && (
            <div className="text-center py-10 text-stone-400 text-sm">
              No entries match &quot;{q}&quot;
            </div>
          )}
          {!loading && !q && (
            <div className="text-center py-10 text-stone-400 text-sm">
              Type a counterparty name, a note keyword, or an amount to search.
            </div>
          )}
          {!loading && results.length > 0 && (
            <div className="divide-y divide-stone-200/50">
              {results.map((e) => (
                <button
                  key={e.id}
                  onClick={() => goto(e.from_account_id)}
                  className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-white/60 transition-colors"
                  data-testid={`search-result-${e.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {nameById[e.from_account_id] || "?"}
                      <span className="text-stone-400">→</span>
                      {nameById[e.to_account_id] || "?"}
                      {e.status === "finalized" && (
                        <Lock size={10} className="text-stone-400 shrink-0" />
                      )}
                    </div>
                    {e.note && (
                      <div className="text-xs text-stone-500 truncate mt-0.5">{e.note}</div>
                    )}
                    <div className="text-[10px] text-stone-400 mt-0.5">
                      {new Date(e.created_at).toLocaleDateString()} · Posted by{" "}
                      {e.created_by_name || "—"}
                    </div>
                  </div>
                  <div
                    className={`font-serif text-xl shrink-0 ${
                      e.amount >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {e.amount >= 0 ? "+" : ""}
                    {num(e.amount)}
                  </div>
                  <ArrowRight size={14} className="text-stone-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
